import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import {
  PartnerDto,
  RewardDefinitionDto,
  RewardDefinitionUpsertDto,
  RewardPoolDto,
} from '@/common/models/gamification'
import {
  createRewardDefinition,
  deleteRewardDefinition,
  getPartners,
  getRewardDefinitions,
  getRewardPools,
  updateRewardDefinition,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { CreateRewardDefinitionModal } from './CreateRewardDefinitionModal'
import { translationTitle } from './shared/labels'

export const RewardDefinitionsPage: React.FC = () => {
  const { festivalName, editionId } = useParams()
  const navigate = useNavigate()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [definitions, setDefinitions] = useState<RewardDefinitionDto[]>([])
  const [partners, setPartners] = useState<PartnerDto[]>([])
  const [pools, setPools] = useState<RewardPoolDto[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<RewardDefinitionDto | null>(null)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = (e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }

  const notify = (msg: string) => {
    setIsError(false)
    setMessage(msg)
    setShowMessageBox(true)
  }

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      setLoading(true)
      const [
        { data: definitionsData },
        { data: partnersData },
        { data: poolsData },
      ] = await Promise.all([
        getRewardDefinitions(editionId),
        getPartners(editionId),
        getRewardPools(),
      ])
      setDefinitions(definitionsData ?? [])
      setPartners(partnersData ?? [])
      setPools(poolsData ?? [])
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [editionId])

  useEffect(() => {
    getData()
  }, [getData])

  const openCreate = () => {
    setEditing(null)
    setCreateOpen(true)
  }

  const openEdit = (row: RewardDefinitionDto) => {
    if (!hasEditPermission) return
    setEditing(row)
    setCreateOpen(true)
  }

  const handleSubmit = async (
    values: RewardDefinitionUpsertDto,
    id?: string,
  ) => {
    if (!editionId) return
    try {
      setLoading(true)
      if (id) {
        await updateRewardDefinition(id, values)
        notify('Reward updated successfully')
      } else {
        await createRewardDefinition(editionId, values)
        notify('Reward created successfully')
      }
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setLoading(true)
      await deleteRewardDefinition(id)
      notify('Reward deleted successfully')
      await getData()
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }

  const partnerName = useCallback(
    (partnerId?: string | null) =>
      partners.find((p) => p.id === partnerId)?.name ?? '—',
    [partners],
  )

  const poolName = useCallback(
    (rewardPoolId?: string | null) => {
      if (!rewardPoolId) return '—'
      const pool = pools.find((p) => p.id === rewardPoolId)
      if (!pool) return '—'
      const partner = partners.find((p) => p.id === pool.partnerId)
      return partner?.name ?? pool.id
    },
    [pools, partners],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: 'Title',
        accessor: 'title',
        render: (row: RewardDefinitionDto) => (
          <span className="text-monochrome-1">
            {translationTitle(row.translations, `${row.typeKey} reward`)}
          </span>
        ),
      },
      {
        title: 'Type',
        accessor: 'typeKey',
        render: (row: RewardDefinitionDto) => (
          <span className="text-monochrome-2">{row.typeKey}</span>
        ),
      },
      {
        title: 'Partner',
        accessor: 'partnerId',
        render: (row: RewardDefinitionDto) => (
          <span className="text-monochrome-2">
            {partnerName(row.partnerId)}
          </span>
        ),
      },
      {
        title: 'Point Price',
        accessor: 'pointPrice',
        render: (row: RewardDefinitionDto) => (
          <span className="text-monochrome-2">{row.pointPrice ?? '—'}</span>
        ),
      },
      {
        title: 'Display Only',
        accessor: 'displayOnly',
        render: (row: RewardDefinitionDto) => (
          <Checkbox checked={row.displayOnly} disabled />
        ),
      },
      {
        title: 'Pool',
        accessor: 'rewardPoolId',
        render: (row: RewardDefinitionDto) => (
          <span className="text-monochrome-2">
            {poolName(row.rewardPoolId)}
          </span>
        ),
      },
      {
        title: 'Rules',
        accessor: '',
        render: (row: RewardDefinitionDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              navigate(
                `/gamification/rewards/${festivalName}/${editionId}/${row.id}`,
              )
            }}
          >
            Rules
          </a>
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row: RewardDefinitionDto) => (
          <a
            className="view text-accent-1 underline"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEdit(row)
            }}
          >
            Edit
          </a>
        ),
      },
    ],
    [
      hasEditPermission,
      partnerName,
      poolName,
      navigate,
      festivalName,
      editionId,
    ],
  )

  return (
    <>
      <Header
        pageTitle="Gamification"
        actions={
          hasEditPermission && (
            <Button variant="primary-bordered" onClick={openCreate}>
              Add Reward
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
              />
            </Button>
          )
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle icon="gift" title="Rewards" className="mb-5" />
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={definitions}
          noDataMessage="No rewards yet"
        />
      </Card>

      <CreateRewardDefinitionModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        definition={editing}
        partners={partners}
        pools={pools}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
